import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { CalendarDays, Plus, Trash2, User, Loader2, Minus, Users, Link2, CheckCircle2, XCircle, Video, Phone } from "lucide-react";
import { toast } from "sonner";

const HOURS = [8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19];

export default function CoachAgenda() {
  const [slots, setSlots] = useState([]);
  const [date, setDate] = useState("");
  const [hour, setHour] = useState(10);
  const [adding, setAdding] = useState(false);
  const [places, setPlaces] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [gcal, setGcal] = useState(null);
  const [gcalBusy, setGcalBusy] = useState(false);
  const [mode, setMode] = useState("visio");

  const load = () => {
    api.get("/coach/slots").then((r) => setSlots(r.data.slots || []));
    api.get("/coach/settings/premium-places").then((r) => setPlaces(r.data.available));
    api.get("/coach/waitlist").then((r) => setWaitlist(r.data.waitlist || [])).catch(() => {});
    api.get("/oauth/calendar/status").then((r) => setGcal(r.data)).catch(() => setGcal(null));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cal = params.get("calendar");
    if (cal === "connected") { toast.success("Google Calendar connecté. Les rendez-vous s'ajouteront automatiquement."); }
    else if (cal === "error") { toast.error("Connexion Google Calendar échouée. Réessaie."); }
    if (cal) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const connectGcal = async () => {
    setGcalBusy(true);
    try {
      const r = await api.get("/oauth/calendar/login");
      window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Google Calendar indisponible"); setGcalBusy(false); }
  };

  const disconnectGcal = async () => {
    if (!window.confirm("Déconnecter Google Calendar ? Les futurs rendez-vous ne seront plus ajoutés automatiquement.")) return;
    setGcalBusy(true);
    try { await api.post("/oauth/calendar/disconnect"); toast.success("Google Calendar déconnecté"); load(); }
    catch { toast.error("Erreur"); }
    finally { setGcalBusy(false); }
  };

  const addSlot = async () => {
    if (!date) { toast.error("Choisis une date"); return; }
    setAdding(true);
    try { await api.post("/coach/slots", { date, hour: Number(hour), mode }); toast.success("Créneau ajouté"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setAdding(false); }
  };

  const delSlot = async (id) => {
    try { await api.delete(`/coach/slots/${id}`); toast.success("Créneau supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const savePlaces = async (val) => {
    const v = Math.max(0, val);
    setPlaces(v);
    try { await api.post("/coach/settings/premium-places", { available: v }); }
    catch { toast.error("Erreur"); }
  };

  const grouped = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const today = new Date().toISOString().slice(0, 10);

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium flex items-center gap-2"><CalendarDays size={22} strokeWidth={1.6} /> Agenda & disponibilités</h1>
        <p className="mt-1 text-sm text-obsidian/50">Ouvre tes créneaux de rendez-vous. Seuls les créneaux ouverts ici sont proposés aux coureurs (Premium ou RDV à la carte).</p>

        {/* GOOGLE CALENDAR */}
        <div className="mt-6 bg-white border border-border p-6 max-w-2xl" data-testid="gcal-card">
          <div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50 flex items-center gap-2"><CalendarDays size={14} /> Google Calendar</div>
          {gcal && !gcal.configured ? (
            <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="gcal-not-configured">
              Intégration non configurée. Renseigne les secrets GOOGLE_CALENDAR_CLIENT_ID et GOOGLE_CALENDAR_CLIENT_SECRET, puis republie l'application.
            </p>
          ) : gcal && gcal.connected ? (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="gcal-connected">
              <span className="inline-flex items-center gap-2 text-sm text-sage font-medium"><CheckCircle2 size={16} /> Connecté{gcal.email ? ` · ${gcal.email}` : ""}</span>
              <span className="text-sm text-obsidian/50">Chaque réservation crée un événement d'1h sur ton calendrier.</span>
              <button data-testid="gcal-disconnect" onClick={disconnectGcal} disabled={gcalBusy}
                className="ml-auto rounded-sm border border-border px-3 py-1.5 text-sm inline-flex items-center gap-2 hover:bg-obsidian/5 disabled:opacity-60">
                <XCircle size={14} /> Déconnecter
              </button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-sm text-obsidian/50">Connecte ton compte pour créer automatiquement un événement d'1h à chaque rendez-vous réservé.</p>
              <button data-testid="gcal-connect" onClick={connectGcal} disabled={gcalBusy || (gcal && !gcal.configured)}
                className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {gcalBusy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Connecter Google Calendar
              </button>
            </div>
          )}
        </div>

        {/* PLACES PREMIUM */}
        <div className="mt-6 bg-white border border-border p-6 max-w-md">
          <div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50 flex items-center gap-2"><Users size={14} /> Places Premium disponibles</div>
          <p className="mt-1 text-sm text-obsidian/50">Nombre affiché aux coureurs sur la page des offres, avant achat.</p>
          {waitlist.filter((w) => w.status === "waiting").length > 0 && (
            <p data-testid="waitlist-count" className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full inline-block px-3 py-1">
              {waitlist.filter((w) => w.status === "waiting").length} coureur(s) en liste d'attente · ils seront alertés dès qu'une place passe au-dessus de 0
            </p>
          )}
          <div className="mt-4 flex items-center gap-4">
            <button data-testid="places-decrement" onClick={() => savePlaces((places || 0) - 1)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-obsidian/5"><Minus size={16} /></button>
            <input data-testid="places-input" type="number" min={0} value={places ?? ""} onChange={(e) => savePlaces(Number(e.target.value))}
              className="w-20 text-center font-display text-3xl font-light border-b border-border focus:outline-none focus:border-obsidian" />
            <button data-testid="places-increment" onClick={() => savePlaces((places || 0) + 1)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-obsidian/5"><Plus size={16} /></button>
          </div>
        </div>

        {/* AJOUT DE CRÉNEAU */}
        <div className="mt-6 bg-white border border-border p-6">
          <div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">Ouvrir un créneau</div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Date</label>
              <input data-testid="slot-date" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)}
                className="rounded-sm border border-border px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Heure</label>
              <select data-testid="slot-hour" value={hour} onChange={(e) => setHour(e.target.value)}
                className="rounded-sm border border-border px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none">
                {HOURS.map((h) => <option key={h} value={h}>{h}h00</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-obsidian/50 mb-1">Format</label>
              <select data-testid="slot-mode" value={mode} onChange={(e) => setMode(e.target.value)}
                className="rounded-sm border border-border px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none">
                <option value="visio">Visio (Google Meet)</option>
                <option value="phone">Téléphone</option>
              </select>
            </div>
            <button data-testid="add-slot-button" onClick={addSlot} disabled={adding}
              className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter
            </button>
          </div>
        </div>

        {/* LISTE DES CRÉNEAUX */}
        <div className="mt-8 space-y-6">
          {Object.keys(grouped).length === 0 && <p className="text-sm text-obsidian/40">Aucun créneau ouvert. Ajoute une disponibilité ci-dessus.</p>}
          {Object.entries(grouped).map(([d, items]) => (
            <div key={d}>
              <div className="text-sm font-medium text-obsidian/70 mb-2">{new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {items.map((s) => (
                  <div key={s.slot_id} data-testid={`agenda-slot-${s.slot_id}`}
                    className={`rounded-lg border px-4 py-3 flex items-center justify-between ${s.status === "booked" ? "border-sage/40 bg-sage/8" : "border-border bg-white"}`}>
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1.5">{s.hour}h00
                        <span data-testid={`slot-mode-badge-${s.slot_id}`} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${s.mode === "phone" ? "bg-obsidian/8 text-obsidian/70" : "bg-sage/15 text-sage"}`}>
                          {s.mode === "phone" ? <><Phone size={9} /> Tél</> : <><Video size={9} /> Visio</>}
                        </span>
                      </div>
                      {s.status === "booked" ? (
                        <div className="mt-0.5 space-y-0.5">
                          <div className="text-xs text-sage flex items-center gap-1"><User size={11} /> {s.client_name || "Réservé"}</div>
                          {s.client_phone && <a href={`tel:${s.client_phone}`} data-testid={`slot-phone-${s.slot_id}`} className="text-xs text-obsidian/60 flex items-center gap-1 hover:text-obsidian"><Phone size={11} /> {s.client_phone}</a>}
                          {s.client_note && <div className="text-xs text-obsidian/45 italic">« {s.client_note} »</div>}
                        </div>
                      ) : (
                        <div className="text-xs text-obsidian/40 mt-0.5">Disponible</div>
                      )}
                    </div>
                    <button data-testid={`delete-slot-${s.slot_id}`} onClick={() => delSlot(s.slot_id)}
                      className="p-1.5 rounded-sm text-obsidian/40 hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </CoachLayout>
  );
}
